<nav class="w-full overflow-x-hidden" [attr.aria-label]="ariaLabel()">
  <div class="flex transition-transform duration-200 ease-in-out" [style.transform]="translateStyle()" (transitionend)="onSlideEnd($event)">
    @for (parent of layers(); track trackLayer($index, parent); let layerIndex = $index) {
      @let layerItems = parent ? parent.items : items();
      @let isDrilledLayer = !!parent;
      <!-- All-or-nothing: thumbnails only render when every item of the layer has one, so no gaps appear. -->
      @let layerShowsPhotos = isDrilledLayer && allItemsHavePhoto(layerItems);
      <div class="flex w-full shrink-0 flex-col" [attr.aria-hidden]="layerIndex !== stack().length ? true : null">
        <!-- BACK-BAR (drilled views only) -->
        @if (parent) {
          <button
            type="button"
            class="flex w-full cursor-pointer flex-row items-center gap-3 px-2"
            [class]="menuItemClasses()"
            [ngClass]="[hoverSubItemClasses(), menuItemColorClasses()]"
            (click)="drillBack()"
            [attr.aria-label]="backLabel()"
          >
            <gs-icon iconName="caret-left" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
            <span class="flex-1 text-left">{{ backLabel() }}</span>
          </button>
          <hr class="border-medium-shine my-2" />
          @if (parent.linkUrl) {
            @let isParentExternal = parent.linkUrl.startsWith('http');
            @let parentActive = isItemActive(parent);
            <a
              [href]="parent.linkUrl"
              [target]="isParentExternal ? '_blank' : (parent.linkTarget ?? '_self')"
              [attr.rel]="isParentExternal ? 'noopener noreferrer' : null"
              class="focus-visible:ring-primary-main flex w-full items-center px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              [class]="menuItemClasses()"
              [ngClass]="[
                hoverSubItemClasses(),
                (parentActive && activeMenuItemClasses()) || menuItemColorClasses(),
                parentActive && activeUnderlined() ? 'underline' : ''
              ]"
              [attr.aria-current]="parentActive ? 'page' : null"
              (click)="itemActivated.emit(parent)"
            >
              {{ parent.label }}
            </a>
          }
        }

        <!-- ITEMS LIST -->
        <ul class="flex w-full flex-col" role="menu">
          @for (item of layerItems; track trackItem($index, item)) {
            @let active = isItemOrDescendantActive(item);
            @let ariaCurrent = isItemActive(item) ? 'page' : null;
            @let hasChildren = item.items.length > 0;
            @let showPhoto = layerShowsPhotos;
            @let showGlyph = !isDrilledLayer && !!item.glyph;
            @let hoverClasses = isDrilledLayer ? hoverSubItemClasses() : hoverRootItemClasses();
            <!-- Active and base colors are Tailwind utilities of equal specificity — never apply both. -->
            @let activeClasses =
              active ? (isDrilledLayer ? activeMenuItemClasses() : activeRootMenuItemClasses() || activeMenuItemClasses()) : '';
            @let itemColorClasses = activeClasses || menuItemColorClasses();
            @let layerPadding = isDrilledLayer ? 'px-2' : '';
            @let underlineClass = active && activeUnderlined() ? 'underline' : '';
            <li role="none">
              @if (hasChildren) {
                <button
                  type="button"
                  role="menuitem"
                  class="focus-visible:ring-primary-main flex w-full items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  [class]="menuItemClasses()"
                  [ngClass]="[layerPadding, hoverClasses, itemColorClasses, underlineClass]"
                  [attr.aria-current]="ariaCurrent"
                  aria-haspopup="menu"
                  (click)="activate(item, $event)"
                  (keydown.enter)="activate(item, $event)"
                  (keydown.space)="activate(item, $event)"
                >
                  <span class="flex flex-1 items-center">
                    @if (showPhoto) {
                      <gs-optimized-image
                        [photo]="item.photo"
                        size="small"
                        mode="fill"
                        sizes="48px"
                        [alt]="item.photo!.alt ?? item.photo!.title ?? item.label"
                        [containerClass]="'size-12 shrink-0 rounded-lg ' + photoMarginClass()"
                        objectFit="object-cover"
                      />
                    } @else if (showGlyph) {
                      <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                        <gs-icon [iconName]="item.glyph!" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
                      </span>
                    }
                    <span class="text-left">{{ item.label }}</span>
                  </span>
                  <gs-icon iconName="caret-right" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
                </button>
              } @else if (item.linkUrl) {
                @let isExternalUrl = item.linkUrl.startsWith('http');
                <a
                  role="menuitem"
                  [href]="item.linkUrl"
                  [target]="isExternalUrl ? '_blank' : (item.linkTarget ?? '_self')"
                  [attr.rel]="isExternalUrl ? 'noopener noreferrer' : null"
                  [attr.aria-current]="ariaCurrent"
                  class="focus-visible:ring-primary-main flex w-full items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  [class]="menuItemClasses()"
                  [ngClass]="[layerPadding, hoverClasses, itemColorClasses, underlineClass]"
                  (click)="itemActivated.emit(item)"
                >
                  @if (showPhoto) {
                    <gs-optimized-image
                      [photo]="item.photo"
                      size="small"
                      mode="fill"
                      sizes="48px"
                      [alt]="item.photo!.alt ?? item.photo!.title ?? item.label"
                      [containerClass]="'size-12 shrink-0 rounded-lg ' + photoMarginClass()"
                      objectFit="object-cover"
                    />
                  } @else if (showGlyph) {
                    <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                      <gs-icon [iconName]="item.glyph!" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
                    </span>
                  }
                  {{ item.label }}
                </a>
              } @else {
                <span
                  role="menuitem"
                  [attr.aria-disabled]="true"
                  [attr.tabindex]="-1"
                  class="flex w-full cursor-not-allowed items-center opacity-60"
                  [class]="menuItemClasses()"
                  [ngClass]="[layerPadding, menuItemColorClasses()]"
                >
                  @if (showPhoto) {
                    <gs-optimized-image
                      [photo]="item.photo"
                      size="small"
                      mode="fill"
                      sizes="48px"
                      [alt]="item.photo!.alt ?? item.photo!.title ?? item.label"
                      [containerClass]="'size-12 shrink-0 rounded-lg ' + photoMarginClass()"
                      objectFit="object-cover"
                    />
                  } @else if (showGlyph) {
                    <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                      <gs-icon [iconName]="item.glyph!" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
                    </span>
                  }
                  {{ item.label }}
                </span>
              }
            </li>
          }
        </ul>
      </div>
    }
  </div>
</nav>
